import React from 'react';
import { Button, Hint, InfoIcon, RefreshIcon, SearchIcon } from '../../components/ui';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { CrewBackButton } from './CrewBackButton';

export type CrewReportFilterWidth = 'sm' | 'md' | 'lg' | 'xl' | 'fill';

/** Fixed width for vessel selects across crew lists and reports. */
export const CREW_VESSEL_FILTER_WIDTH: CrewReportFilterWidth = 'lg';

/** Class for vessel selects outside {@link CrewReportFilterField}. */
export const CREW_VESSEL_FILTER_CLASS = 'w-[14rem] max-w-full';

const WIDTH_CLASS: Record<CrewReportFilterWidth, string> = {
  sm: 'w-[8.75rem]',
  md: 'w-[10.5rem]',
  lg: 'w-[14rem]',
  xl: 'w-[18rem]',
  fill: 'min-w-[12rem] flex-1 max-w-sm',
};

export interface CrewReportFilterFieldProps {
  label: string;
  htmlFor?: string;
  width?: CrewReportFilterWidth;
  className?: string;
  children: React.ReactNode;
}

/** Consistent label + control stack for crew report filters (matches accounting report field labels). */
export const CrewReportFilterField: React.FC<CrewReportFilterFieldProps> = ({
  label,
  htmlFor,
  width = 'md',
  className,
  children,
}) => {
  const { colors } = useTheme();
  return (
    <div className={cn('min-w-0', WIDTH_CLASS[width], className)}>
      <label
        htmlFor={htmlFor}
        className={cn('mb-0.5 block text-sm font-medium leading-tight', colors.text)}
      >
        {label}
      </label>
      {children}
    </div>
  );
};

export interface CrewReportHint {
  /** Short heading shown in the popover. */
  title: string;
  /** Longer explanation (plain text or rich content). */
  body: React.ReactNode;
}

export interface CrewReportSearchHeaderProps {
  /** Report help shown via the info control. */
  hint?: CrewReportHint | string;
  children: React.ReactNode;
  onSearch: () => void;
  searchDisabled?: boolean;
  searchLabel?: string;
  onRefresh?: () => void;
  refreshDisabled?: boolean;
  loading?: boolean;
  /** Applied-criteria summary shown under the filters. */
  context?: React.ReactNode;
  className?: string;
  /** Hide the shared back control (rare). */
  hideBack?: boolean;
  /** Override back fallback when history cannot go back. */
  backFallback?: string;
  /** Extra controls after Search/Refresh (far right). */
  trailingActions?: React.ReactNode;
}

function normalizeHint(hint: CrewReportHint | string | undefined): CrewReportHint | null {
  if (!hint) return null;
  if (typeof hint === 'string') return { title: 'About this report', body: hint };
  return hint;
}

/** Info / Help control used on crew list, reports, and Crew Changes Cost. */
export const CrewHelpHint: React.FC<{ hint: CrewReportHint; className?: string }> = ({
  hint,
  className,
}) => {
  const { colors } = useTheme();

  return (
    <div className={cn('relative flex h-10 items-center self-end', className)}>
      <Hint content={hint.body} label={hint.title} side="bottom" maxWidth={360} delay={120}>
        <button
          type="button"
          aria-label={hint.title}
          className={cn(
            'group inline-flex h-10 items-center gap-2 rounded-lg border px-2.5 transition-colors',
            colors.border,
            colors.textMuted,
            colors.interactiveHover,
          )}
        >
          <span
            className={cn(
              'inline-flex h-6 w-6 items-center justify-center rounded-full',
              colors.surfaceHover,
              colors.textMuted,
            )}
          >
            <InfoIcon className="h-3.5 w-3.5" />
          </span>
          <span className="hidden sm:inline text-sm font-medium">Help</span>
        </button>
      </Hint>
    </div>
  );
};

/** @deprecated Prefer {@link CrewHelpHint} */
const CrewReportHintControl = CrewHelpHint;

/**
 * Shared filter / search chrome for crew reports.
 * Use with {@link CrewReportFilterField} for consistent field layout.
 */
const CrewReportSearchHeader: React.FC<CrewReportSearchHeaderProps> = ({
  hint,
  children,
  onSearch,
  searchDisabled = false,
  searchLabel = 'Search',
  onRefresh,
  refreshDisabled = false,
  loading = false,
  context,
  className,
  hideBack = false,
  backFallback,
  trailingActions,
}) => {
  const { colors } = useTheme();
  const normalizedHint = normalizeHint(hint);

  return (
    <section
      className={cn(
        'rounded-xl border shadow-sm',
        colors.surface,
        colors.border,
        className,
      )}
    >
      <form
        className="px-4 py-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!searchDisabled && !loading) onSearch();
        }}
      >
        <div className="flex flex-wrap items-end gap-3">
          {!hideBack ? <CrewBackButton fallbackPath={backFallback} /> : null}
          {normalizedHint ? <CrewReportHintControl hint={normalizedHint} /> : null}
          {children}
          <div className="flex h-10 items-center gap-2 ml-auto">
            <Button type="submit" disabled={searchDisabled || loading}>
              <SearchIcon className="h-4 w-4" />
              {searchLabel}
            </Button>
            {onRefresh ? (
              <Button
                type="button"
                variant="outline"
                onClick={onRefresh}
                disabled={refreshDisabled || loading}
                loading={loading}
                title="Refresh"
              >
                <RefreshIcon className="h-4 w-4" />
                Refresh
              </Button>
            ) : null}
            {trailingActions}
          </div>
        </div>
      </form>

      {context != null && context !== '' ? (
        <div
          className={cn(
            'border-t px-4 py-2.5 text-sm rounded-b-xl',
            colors.headerBg,
            colors.border,
            colors.textSecondary,
          )}
        >
          {context}
        </div>
      ) : null}
    </section>
  );
};

export default CrewReportSearchHeader;
